/**
 * RoleEditorDialog — create / edit / view a role (ADR-0030).
 *
 * System (`isSystem`) roles open read-only (the matrix is disabled, no Save) —
 * the operator duplicates them to customise. Custom roles are fully editable.
 * Authorisation is server-enforced (`permissionGate('roles.manage')`); the
 * dialog surfaces the wire error inline.
 */
import * as React from "react";
import {
  Alert,
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@flint/ui";
import { ALL_PERMISSIONS, PERMISSIONS } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.ts";
import { PermissionMatrix } from "./permission-matrix.tsx";

export interface EditableRole {
  id: string;
  key: string;
  name: string;
  description: string | null;
  permissions: string[];
  isSystem: boolean;
  departmentId: string | null;
}

const NO_DEPT = "__none__";

export interface RoleEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The role to edit/view, or `null` to create a new one. */
  role: EditableRole | null;
  departments: ReadonlyArray<{ id: string; name: string }>;
  onSaved: () => void;
}

export function RoleEditorDialog({ open, onOpenChange, role, departments, onSaved }: RoleEditorDialogProps) {
  const isView = role?.isSystem === true;
  const isEdit = role !== null && !isView;

  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [departmentId, setDepartmentId] = React.useState<string>(NO_DEPT);
  const [permissions, setPermissions] = React.useState<Set<string>>(new Set());

  // System roles like Owner store `"*"` (wildcard) — expand to the full
  // catalog so the matrix + counter honestly show full access instead of
  // "1 granted / 0 of N" per area.
  const hasWildcard = role?.permissions?.includes(ALL_PERMISSIONS) ?? false;

  React.useEffect(() => {
    if (!open) return;
    setName(role?.name ?? "");
    setDescription(role?.description ?? "");
    setDepartmentId(role?.departmentId ?? NO_DEPT);
    const rolePerms = role?.permissions ?? [];
    setPermissions(new Set(rolePerms.includes(ALL_PERMISSIONS) ? PERMISSIONS : rolePerms));
  }, [open, role]);

  const utils = trpc.useUtils();
  const onDone = async () => {
    await utils.roles.list.invalidate();
    onSaved();
    onOpenChange(false);
  };
  const create = trpc.roles.create.useMutation({ onSuccess: onDone });
  const update = trpc.roles.update.useMutation({ onSuccess: onDone });
  const pending = create.isPending || update.isPending;
  const wireError = create.error?.message ?? update.error?.message ?? null;

  const toggle = (p: string, next: boolean) =>
    setPermissions((prev) => {
      const s = new Set(prev);
      if (next) s.add(p);
      else s.delete(p);
      return s;
    });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isView) {
      onOpenChange(false);
      return;
    }
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      permissions: [...permissions],
      departmentId: departmentId === NO_DEPT ? null : departmentId,
    };
    if (isEdit && role) update.mutate({ roleId: role.id, ...payload });
    else create.mutate(payload);
  };

  const title = isView ? `${role?.name} · built-in role` : isEdit ? "Edit role" : "New role";
  const ready = isView || (name.trim() !== "" && permissions.size > 0);

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[88vh] flex-col sm:max-w-[680px]">
        <form onSubmit={submit} noValidate className="flex min-h-0 flex-col">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {isView
                ? "Built-in roles are read-only. Create a custom role to grant a different set of permissions."
                : "A role is a reusable bundle of permissions. Assign it to teammates on the Members tab."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto py-4">
            {!isView ? (
              <div className="grid gap-3.5 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="role-name">Name</Label>
                  <Input
                    id="role-name"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                    placeholder="e.g. Floor Supervisor"
                    maxLength={80}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="role-dept">Department</Label>
                  <Select value={departmentId} onValueChange={setDepartmentId}>
                    <SelectTrigger id="role-dept">
                      <SelectValue placeholder="No department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_DEPT}>No department</SelectItem>
                      {departments.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                  <Label htmlFor="role-desc">Description</Label>
                  <Input
                    id="role-desc"
                    value={description}
                    onChange={(e) => setDescription(e.currentTarget.value)}
                    placeholder="What this role is for."
                    maxLength={200}
                  />
                </div>
              </div>
            ) : role?.description ? (
              <p className="text-sm text-muted">{role.description}</p>
            ) : null}

            <div className="flex items-center justify-between">
              <Label>Permissions</Label>
              <span className="font-mono text-xs text-muted">{permissions.size} granted</span>
            </div>
            {isView && hasWildcard ? (
              <Alert
                intent="info"
                title="Full access"
                description="Owner has every permission in the workspace, including future ones added by upgrades."
              />
            ) : null}
            <PermissionMatrix
              value={permissions}
              readOnly={isView}
              onToggle={toggle}
              onToggleArea={(perms, next) =>
                setPermissions((prev) => {
                  const s = new Set(prev);
                  for (const p of perms) {
                    if (next) s.add(p);
                    else s.delete(p);
                  }
                  return s;
                })
              }
            />

            {wireError ? <Alert intent="bad" title="Couldn't save role" description={wireError} /> : null}
          </div>

          <DialogFooter>
            <Button type="button" intent="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
              {isView ? "Done" : "Cancel"}
            </Button>
            {!isView ? (
              <Button type="submit" disabled={!ready || pending}>
                {pending ? "Saving…" : isEdit ? "Save changes" : "Create role"}
              </Button>
            ) : null}
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}
